import { CompanyResponse } from "./company.type";
import { ContentPaginatedInterface } from "./content.type";
import { ORDER_TYPES } from "./enums";
import { FileResponse } from "./file.type";
import {
  MaterialResponse,
  MaterialSiteRef,
  MaterialType,
} from "./material.type";
import { QuotedLine, ServiceDetail } from "./serviceDetail.type";
import { SiteResponse } from "./site.type";
import { UserResponse } from "./user.type";

export enum OrderState {
  SUCCEED = "SUCCEED",
  CLOSED = "CLOSED",
  OPEN = "OPEN",
  PENDING = "PENDING",
  CONSULTATION = "CONSULTATION",
  CANCELLED = "CANCELLED",
  DRAFT = "DRAFT",
  REJECTED = "REJECTED",
  DELIVERED = "DELIVERED",
}

export interface MultipleOrderResponse {
  createdAt: string;
  updatedAt: string;
  id: string;
  name: string;
}

/**
 * Shape returned by the OrderController endpoints (single-order read, bidding lists, joint-order
 * maps, create/update writes), now backed by the backend `OrderResponse` DTO rather than the raw
 * `Order` entity. The DTO strips the sensitive company/user graph: on the wire `company` is only
 * `{ id, name }`, and `user` / each `suppliers[]` element is `{ id, firstName, lastName, fullName,
 * email, company: { id, name } }` — no `taxId` / `bankingInfos` / `annualRevenue` / `password`.
 *
 * The field TYPES here stay `CompanyResponse` / `UserResponse` on purpose: this interface is ALSO
 * reused for `offer.order` (see offer.type.ts). Sub-flow C now DTOs the Offer read endpoints and
 * projects `offer.order` through THIS lean `OrderResponse`, so on the wire the order's company is
 * `{id,name}` and users are lean — but the still-Tailwind seller screens (SellerOffer /
 * JointSellerOffer / Offer) keep reading `offer.order.company.taxId`,
 * `offer.order.user.phone` and `offer.order.incoTerm` off this type. Narrowing company/user to the
 * lean shape (or dropping `incoterm`) would break those screens' typecheck, so the width stays and
 * `incoterm` is kept until the T5/T6 seller-screen migration consumes the lean shape (those two
 * wide reads already read `undefined` at runtime under the lean projection — the accepted, deferred
 * degradation). The dead `fileUrl` alias, which had no reader at all, WAS dropped. The lean reads
 * (`company.name`, `user.fullName`, `suppliers[].company?.name`) are all satisfied by the wider type.
 */
export interface OrderResponse extends ServiceDetail {
  quotedLines?: QuotedLine[];
  /** The buyer collects at the supplier's address: offers carry a pickup address, no logistics. */
  buyerPicksUp?: boolean;
  active: boolean;
  hasOfferSelected: boolean;
  address?: string;
  brand?: string;
  comment?: string;
  company: CompanyResponse;
  createdAt: string;
  currency: string;
  deleted: boolean;
  deliveryPlace: SiteResponse | null;
  // Also sent by the DTO (lean site, no nested company); the launch flow reads only id/name.
  requestingSite?: SiteResponse | null;
  details?: string;
  email: string;
  endSupplyDate: string;
  files: FileResponse[];
  // Per-item (line-level) attachments from the originating Solped item, kept
  // separate from the request-level general attachments in `files`.
  itemFiles?: FileResponse[];
  finishDate: string;
  // The entity/DTO serializes the attachment link as `plicationUrl`; the old `fileUrl` field was
  // never on the wire and had no reader, so it was dropped in sub-flow C.
  plicationUrl?: string;
  fullname: string;
  incoTerm?: string;
  // The entity/DTO serializes the incoterm as `incoterm` (lowercase t).
  incoterm?: string;
  id: string;
  initialPrice?: number;
  internalCode?: string;
  material: MaterialResponse;
  measurementUnit: string;
  minimumDecrement?: number;
  multipleOrder: MultipleOrderResponse | null;
  name: string;
  paymentMethod: string;
  paymentTerm: number;
  purpose?: string;
  quantity: number;
  spot: boolean;
  startDate: string;
  startSupplyDate: string;
  // The instants bidding opens and closes (ISO). startDate/finishDate are their Argentine days.
  opensAt?: string | null;
  closesAt?: string | null;
  // Optional consultation (Q&A) window before bidding opens (service quotations).
  consultationStartDate?: string | null;
  consultationEndDate?: string | null;
  // Quotation-round tracking for second-round launches.
  round?: number;
  previousRoundOrderId?: string | null;
  expireDate: boolean;
  state: OrderState;
  suppliers: UserResponse[];
  tel?: string;
  type: ORDER_TYPES;
  updatedAt: string;
  urgent?: boolean;
  offerSelectionComment?: string;
  offerSelectionFileUrl?: string;
  user: UserResponse;
}

export interface OrderToSubmitRequest extends ServiceDetail {
  id?: string;
  orderId?: string;
  multipleOrderId?: string;
  ean?: string;
  quantity?: string | number;
  material?: MaterialResponse;
  user?: UserResponse;
  initialPrice?: string;
  minimumDecrement?: string;
  company?:
    | CompanyResponse
    | {
        id?: string;
      }
    | string;
  email?: string;
  coin?: string;
  globalMaterialName?: string;
  deliveryFormat?: string;
  owner?: string;
  fullName?: string;
  files?: FileResponse[];
  itemFiles?: FileResponse[];
  // Either a full site (from a solped item's own `site`) or the lean `MaterialSiteRef` the material
  // DTO now carries (Order/OrderByRequest seed it from `material.deliveryPlace`). Only `.id`/`.name`
  // are read off it downstream, both present on either shape.
  deliveryPlace?: SiteResponse | MaterialSiteRef | null;
  address?: string;
  fullname?: string;
  name?: string;
  brand?: string;
  purpose?: string;
  fileUrl?: string;
  measurementUnit?: string;
  incoTerm?: string;
  currency?: string;
  spot?: boolean | string;
  startSupplyDate?: Date;
  endSupplyDate?: Date;
  expireDate?: boolean;
  internalCode?: string;
  type?: ORDER_TYPES | MaterialType;
  state?: OrderState;
  purchaseRequestId?: string;
  purchaseRequestItemId?: string;
  suppliers: (
    | {
        id: string;
        email?: string;
        name?: string;
        companyName?: string;
      }
    | {
        materialId: string;
        emailsList: string;
      }
  )[];
}

export interface AwardOrderRequest {
  offer: {
    createdAt: string;
    updatedAt: string;
    id: string;
    price: number;
    oldPrice: number;
    oldQuantity: number;
    quantity: number;
    deliveryDate: string;
    deliveryFormat: string;
    comment: string;
    fileURL?: string;
    paymentMethod: string;
    order: OrderResponse;
    user: UserResponse;
    multipleOffer?: MultipleOrderResponse;
    selected: boolean;
    active: boolean;
  };
  offerSelectionComment?: string;
  offerSelectionFileUrl?: string;
  replace: boolean;
}

export interface AwardedOrderResponse {
  id: string;
  name: string;
  orderName: string;
  state: string;
  startDate: string;
  finishDate: string;
  comment: string;
  userId: string;
  userName: string;
  companyId: string;
  companyName: string;
  siteId: string;
  siteName: string;
  siteAddress: string;
  multipleOrderId: string;
  multipleOrderName: string;
}

export interface JointOrderResponse {
  [key: string]: OrderResponse[];
}

export interface SimpleOrder {
  type: "SIMPLE";
  simpleOrder: OrderResponse;
  jointOrders: null;
  multipleOrderId: null;
  multipleOrderName: null;
  /** Seller biddings list only: whether the current seller already quoted this order, that offer's
   * id (for routing to the bid view), and how many of the RFQ's line items are offered of the total.
   * Absent on the buyer/RFQ lists. */
  hasOffered?: boolean;
  offerId?: string | null;
  offeredItemCount?: number;
  totalItemCount?: number;
  /** Seller biddings list only: whether THIS seller won any line of the order (its state === SUCCEED
   * and one of their offers is selected). Drives "your offer selected" vs "…not selected". */
  awardedToMe?: boolean;
}

export interface JointOrder {
  type: "JOINT";
  simpleOrder: null;
  jointOrders: OrderResponse[];
  multipleOrderId: string;
  multipleOrderName: string;
  hasOffered?: boolean;
  offerId?: string | null;
  offeredItemCount?: number;
  totalItemCount?: number;
  awardedToMe?: boolean;
}

export interface AllOrdersResponse {
  orders: (SimpleOrder | JointOrder)[];
  totalSimpleOrders: number;
  totalJointOrders: number;
  totalOrders: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalElements: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export type SimpleOrderPaginatedResponse =
  ContentPaginatedInterface<OrderResponse>;
